import { useState } from 'react';
import toast from 'react-hot-toast';
import { RichTextEditor } from '../common/RichTextEditor';
import { Timestamp } from 'firebase/firestore';
import { createArticle, sendArticleNotification, updateArticle } from '../../services/firebase';
import type { Article, ArticleLink } from '../../types';
import { Button } from '../common/Button';
import { CopyLinkButton } from '../common/CopyLinkButton';
import { ImageUrlField } from '../common/ImageUrlField';
import { buildShareUrl } from '../../services/shareLinks';
import { Badge } from '../common/Badge';
import { Modal } from '../common/Modal';
import { extractYouTubeId, youTubeEmbedUrl } from '../../utils/youtube';

type ReviewStatus = 'draft' | 'ready_for_review' | 'rejected' | 'accepted';

/**
 * The body's ceiling, in pixels, shared by both editing modes so switching
 * between them does not resize the page. Fixed rather than viewport-relative on
 * purpose: a `vh` cap collapses the editor to a sliver on a laptop in split
 * screen, and the problem being solved — an unreachable Save button — is as well
 * served by a predictable number.
 */
const BODY_MAX_HEIGHT = 600;

type ArticleFormData = {
  type: 'external' | 'rich';
  title: string;
  subtitle: string;
  excerpt: string;
  body: string;
  externalUrl: string;
  authorName: string;
  authorAvatarUrl: string;
  authorBio: string;
  thumbnailUrl: string;
  headerMediaType: 'image' | 'video' | '';
  headerMediaUrl: string;
  headerMediaAltText: string;
  tags: string;
  category: string;
  readTimeMinutes: string;
  featured: boolean;
  enabled: boolean;
  testMode: boolean;
  visibleWhenAnonymous: boolean;
  pinOrder: string;
  promoted: boolean;
  promotedUntil: string;
  links: ArticleLink[];
  source: string;
  sourceUrl: string;
  disclaimer: string;
};

const emptyForm: ArticleFormData = {
  type: 'rich',
  title: '',
  subtitle: '',
  excerpt: '',
  body: '',
  externalUrl: '',
  authorName: '',
  authorAvatarUrl: '',
  authorBio: '',
  thumbnailUrl: '',
  headerMediaType: '',
  headerMediaUrl: '',
  headerMediaAltText: '',
  tags: '',
  category: '',
  readTimeMinutes: '',
  featured: false,
  enabled: false,
  testMode: false,
  visibleWhenAnonymous: false,
  pinOrder: '',
  promoted: false,
  promotedUntil: '',
  links: [],
  source: '',
  sourceUrl: '',
  disclaimer: '',
};

interface ArticleEditorProps {
  article: Article | null;
  userUid?: string;
  isAdmin: boolean;
  onBack: () => void;
  onSaved: () => void;
}

function articleToForm(article: Article): ArticleFormData {
  return {
    type: article.type,
    title: article.title,
    subtitle: article.subtitle || '',
    excerpt: article.excerpt,
    body: article.body || '',
    externalUrl: article.externalUrl || '',
    authorName: article.author.name,
    authorAvatarUrl: article.author.avatarUrl || '',
    authorBio: article.author.bio || '',
    thumbnailUrl: article.thumbnailUrl,
    headerMediaType: article.headerMedia?.type || '',
    headerMediaUrl: article.headerMedia?.url || '',
    headerMediaAltText: article.headerMedia?.altText || '',
    tags: article.tags.join(', '),
    category: article.category || '',
    readTimeMinutes: article.readTimeMinutes != null ? String(article.readTimeMinutes) : '',
    featured: article.featured,
    enabled: article.enabled,
    testMode: article.testMode === true,
    visibleWhenAnonymous: article.visibleWhenAnonymous === true,
    pinOrder: article.pinOrder != null ? String(article.pinOrder) : '',
    promoted: article.promoted ?? false,
    promotedUntil: article.promotedUntil
      ? toDatetimeLocalString(article.promotedUntil.toDate())
      : '',
    links: article.links || [],
    source: article.source || '',
    sourceUrl: article.sourceUrl || '',
    disclaimer: article.disclaimer || '',
  };
}

function reviewStatusLabel(status: ReviewStatus): string {
  switch (status) {
    case 'draft': return 'Draft';
    case 'ready_for_review': return 'Pending Review';
    case 'rejected': return 'Changes Requested';
    case 'accepted': return 'Accepted';
  }
}

function reviewStatusBadgeVariant(status: ReviewStatus): 'default' | 'info' | 'danger' | 'success' | 'warning' {
  switch (status) {
    case 'draft': return 'default';
    case 'ready_for_review': return 'info';
    case 'rejected': return 'danger';
    case 'accepted': return 'success';
  }
}

export function ArticleEditor({ article, userUid, isAdmin, onBack, onSaved }: ArticleEditorProps) {
  const isCreating = !article;
  // Existing articles without a reviewStatus that are already enabled should
  // be treated as accepted (they predate the review workflow)
  const currentReviewStatus: ReviewStatus =
    article?.reviewStatus || (article?.enabled ? 'accepted' : 'draft');

  const [form, setForm] = useState<ArticleFormData>(
    article ? articleToForm(article) : emptyForm,
  );
  const [saving, setSaving] = useState(false);
  const [bodyEditorMode, setBodyEditorMode] = useState<'visual' | 'html'>('visual');

  /**
   * The body is CAPPED, and expands to full screen on demand.
   *
   * Both modes stop at the same height so switching between them does not jump
   * the page. Left to grow, a long article pushed the form's Save button so far
   * down that reaching it meant scrolling past the entire body — which is the
   * bug, not the unbounded growth itself.
   *
   * Full screen is where the room went. One editor is mounted at a time: while
   * expanded the in-form slot is a placeholder and the modal holds the only
   * instance, both bound to the same `form.body`. Saving therefore cannot differ
   * between the two states, because there is only ever one editor to save from.
   * The cost is a TipTap remount on toggle, which loses the cursor position.
   */
  const [bodyExpanded, setBodyExpanded] = useState(false);

  /**
   * Announcing an article is a separate, deliberate act from enabling it.
   *
   * The trigger used to notify every subscriber the moment an article went
   * enabled, so one enabled by mistake reached the whole user base before anyone
   * could react — and a delivered push cannot be recalled. Enabling now just
   * enables; the announcement happens here, on purpose, twice over: a confirm at
   * the moment of publishing, and a button for whenever the curator gets to it.
   *
   * Held locally rather than re-read, because saving closes this editor and the
   * button's own send has no reload to wait for — so what the server just told us
   * is the freshest thing we have.
   */
  const [notified, setNotified] = useState<{ at: Date | null } | null>(
    article?.notifiedAsPublished
      ? { at: article.notifiedAsPublishedAt?.toDate() ?? null }
      : null,
  );
  const [sending, setSending] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [pendingReview, setPendingReview] = useState<ReviewStatus | undefined>(undefined);

  // Admin review state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');


  const updateForm = (updates: Partial<ArticleFormData>) => {
    setForm((prev) => ({ ...prev, ...updates }));
  };

  const validateForm = (): boolean => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return false;
    }
    if (!form.excerpt.trim()) {
      toast.error('Excerpt is required');
      return false;
    }
    if (!form.thumbnailUrl.trim()) {
      toast.error('A thumbnail image is required — upload one or paste a URL');
      return false;
    }
    if (!form.authorName.trim()) {
      toast.error('Author name is required');
      return false;
    }
    if (form.type === 'external' && !form.externalUrl.trim()) {
      toast.error('External URL is required for external articles');
      return false;
    }
    if (form.type === 'rich' && !form.body.trim()) {
      toast.error('Body is required for rich articles');
      return false;
    }
    if (form.pinOrder !== '') {
      const val = parseInt(form.pinOrder, 10);
      if (isNaN(val) || val < 1 || !Number.isInteger(val)) {
        toast.error('Pin Order must be a positive integer or empty');
        return false;
      }
    }
    if (form.promoted) {
      if (!form.promotedUntil) {
        toast.error('Promotion expiry date is required when promoted');
        return false;
      }
      if (new Date(form.promotedUntil) <= new Date()) {
        toast.error('Promotion expiry must be a future date');
        return false;
      }
    }
    if (form.readTimeMinutes !== '') {
      const val = parseInt(form.readTimeMinutes, 10);
      if (isNaN(val) || val < 1) {
        toast.error('Read time must be a positive number');
        return false;
      }
    }
    if (form.headerMediaType === 'video' && form.headerMediaUrl.trim()) {
      if (!extractYouTubeId(form.headerMediaUrl.trim())) {
        toast.error('Video URL must be a valid YouTube link');
        return false;
      }
    }
    return true;
  };

  const buildArticleData = (overrides?: Partial<Article>) => {
    const tags = form.tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const data: Omit<Article, 'id'> = {
      articleId: article?.articleId || crypto.randomUUID(),
      type: form.type,
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      author: {
        name: form.authorName.trim(),
        ...(form.authorAvatarUrl.trim() && { avatarUrl: form.authorAvatarUrl.trim() }),
        ...(form.authorBio.trim() && { bio: form.authorBio.trim() }),
      },
      tags,
      featured: form.featured,
      // Test mode implies not-published, the same bargain the collection
      // editor strikes: an article only admins should see must stay out of
      // every `enabled == true` list query.
      enabled: isAdmin && !form.testMode ? form.enabled : false,
      // Written as a plain `false` rather than omitted, because `updateArticle`
      // uses `updateDoc` — an omitted key would leave a stale `true` behind and
      // the article would never come out of test mode.
      testMode: isAdmin ? form.testMode : false,
      // Curator-designated, and the toggle is admin-only — so a contributor's
      // save must carry the stored value through rather than resetting it to
      // false, or an author fixing a typo would silently pull their own article
      // off the landing screen. Written unconditionally for the same reason as
      // `testMode`: `updateArticle` uses `updateDoc`, so an omitted key would
      // leave a stale `true` behind.
      visibleWhenAnonymous: isAdmin
        ? form.visibleWhenAnonymous
        : article?.visibleWhenAnonymous === true,
      // Always written, for the same reason as `testMode`: un-ticking Promote
      // used to stop writing the key rather than writing `false`, and an
      // `updateDoc` merge left the stored `true` exactly where it was.
      promoted: form.promoted,
      thumbnailUrl: form.thumbnailUrl.trim(),
      publishedDate: article?.publishedDate || Timestamp.now(),
      updatedDate: Timestamp.now(),
    };

    // Set createdByUid on new articles
    if (isCreating && userUid) {
      data.createdByUid = userUid;
    }

    // Set review status
    if (isCreating) {
      data.reviewStatus = isAdmin ? 'accepted' : 'draft';
    }

    /**
     * Every optional field, written or explicitly erased — never merely omitted.
     *
     * These used to be a tail of `if (form.x) data.x = …`, which meant clearing
     * one simply stopped writing its key. `updateArticle` calls `updateDoc`,
     * which MERGES, so the stored value survived: un-ticking Promote left the
     * article promoted, and "Leave empty to unpin" never unpinned anything.
     *
     * `undefined` is not the fix — `db` is a plain `getFirestore(app)` with no
     * `ignoreUndefinedProperties`, so an undefined value is a hard error on both
     * write paths. So: on CREATE (`setDoc`) an empty field writes nothing, since
     * an absent key is already absent; on UPDATE only an explicit `null` erases.
     */
    const setOrClear = (key: keyof Omit<Article, 'id'>, value: unknown) => {
      const target = data as Record<string, unknown>;
      const isEmpty =
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0);
      if (!isEmpty) target[key] = value;
      else if (!isCreating) target[key] = null;
    };

    setOrClear('subtitle', form.subtitle.trim());
    setOrClear('category', form.category.trim());
    setOrClear('source', form.source.trim());
    setOrClear('sourceUrl', form.sourceUrl.trim());
    setOrClear('disclaimer', form.disclaimer.trim());
    setOrClear('links', form.links);
    setOrClear(
      'readTimeMinutes',
      form.readTimeMinutes === '' ? null : parseInt(form.readTimeMinutes, 10),
    );
    setOrClear('pinOrder', form.pinOrder === '' ? null : parseInt(form.pinOrder, 10));

    // Dependent pair: the expiry belongs to the promotion. Left alone it
    // orphaned, and an unpromoted article kept a promotion end date.
    setOrClear(
      'promotedUntil',
      form.promoted && form.promotedUntil
        ? Timestamp.fromDate(new Date(form.promotedUntil))
        : null,
    );

    // Dependent pair: `type` decides which of these two the article HAS. A
    // rich article switched to external kept its old body, leaving the app an
    // article carrying both, and the mirror left a stale externalUrl.
    setOrClear('body', form.type === 'rich' ? form.body.trim() : null);
    setOrClear('externalUrl', form.type === 'external' ? form.externalUrl.trim() : null);

    // Dependent pair: the media map is only meaningful with a type AND a URL.
    // Cleared as a WHOLE map — `altText` needs no clearing of its own, because a
    // key missing from a replacement map is already an erased key.
    let headerMedia: Article['headerMedia'] | null = null;
    if (form.headerMediaType && form.headerMediaUrl.trim()) {
      let mediaUrl = form.headerMediaUrl.trim();
      if (form.headerMediaType === 'video') {
        const videoId = extractYouTubeId(mediaUrl);
        if (videoId) mediaUrl = youTubeEmbedUrl(videoId);
      }
      headerMedia = {
        type: form.headerMediaType as 'image' | 'video',
        url: mediaUrl,
        ...(form.headerMediaAltText.trim() && { altText: form.headerMediaAltText.trim() }),
      };
    }
    setOrClear('headerMedia', headerMedia);

    // Apply overrides (e.g. reviewStatus changes)
    if (overrides) {
      Object.assign(data, overrides);
    }

    return data;
  };

  /**
   * True when this save is the moment the article becomes publicly visible for
   * the first time and nobody has been told yet.
   *
   * Reads the EFFECTIVE enabled value, not `form.enabled` — test mode forces it
   * false on the way to Firestore, and prompting to announce an article that is
   * about to be saved as disabled would be a lie.
   *
   * A create that arrives enabled counts: `article` is null, so there is no
   * previous `enabled === true`. That is the most direct route to publishing,
   * and it would be the one path that skipped the prompt.
   */
  const isPublishingNow = (): boolean => {
    const nextEnabled = isAdmin && !form.testMode ? form.enabled : false;
    if (!nextEnabled) return false;
    if (article?.notifiedAsPublished) return false;
    // Already-enabled articles are not transitioning; the header button is
    // where they get announced.
    return article?.enabled !== true;
  };

  /** Announce, and report what actually happened. Never throws. */
  const notifySubscribers = async (articleId: string): Promise<boolean> => {
    try {
      const { notifiedCount } = await sendArticleNotification(articleId);
      if (notifiedCount === 0) {
        // The server leaves the article un-announced in this case, so the action
        // stays available rather than being spent on a send that reached nobody.
        toast('No subscribers to notify — nobody has notifications turned on.', {
          icon: 'ℹ️',
        });
        return false;
      }
      toast.success(`Notification sent to ${notifiedCount} user${notifiedCount === 1 ? '' : 's'}`);
      setNotified({ at: new Date() });
      return true;
    } catch (error) {
      // The callable's refusals are written for whoever pressed the button, so
      // show what it said rather than a generic failure.
      const message = error instanceof Error ? error.message : 'Failed to send the notification';
      console.error('Error sending article notification:', error);
      toast.error(message);
      return false;
    }
  };

  // Save as draft (contributor) or save changes (admin)
  const handleSave = async (reviewStatusOverride?: ReviewStatus) => {
    if (!validateForm()) return;

    // Ask before the write, so "Not yet" is a decision about the announcement
    // and not about the save.
    if (isPublishingNow()) {
      setPendingReview(reviewStatusOverride);
      setShowPublishConfirm(true);
      return;
    }

    await persist(reviewStatusOverride, false);
  };

  const persist = async (reviewStatusOverride?: ReviewStatus, alsoNotify = false) => {
    setSaving(true);
    try {
      const overrides: Partial<Article> = {};
      if (reviewStatusOverride) {
        overrides.reviewStatus = reviewStatusOverride;
      }

      const data = buildArticleData(overrides);
      let savedId = article?.id ?? '';
      if (isCreating) {
        savedId = await createArticle(data);
        toast.success(
          reviewStatusOverride === 'ready_for_review'
            ? 'Article submitted for review'
            : 'Article created',
        );
      } else {
        await updateArticle(article.id, data);
        toast.success(
          reviewStatusOverride === 'ready_for_review'
            ? 'Article submitted for review'
            : 'Article updated',
        );
      }
      // After the write, never before: an article that failed to save must not
      // be announced, and the announcement needs the id a create only returns.
      if (alsoNotify && savedId) {
        await notifySubscribers(savedId);
      }

      onSaved();
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  // Admin: accept article
  const handleAccept = async () => {
    if (!article) return;
    setSaving(true);
    try {
      await updateArticle(article.id, { reviewStatus: 'accepted' });
      toast.success('Article accepted');
      onSaved();
    } catch (error) {
      console.error('Error accepting article:', error);
      toast.error('Failed to accept article');
    } finally {
      setSaving(false);
    }
  };

  // Admin: reject article
  const handleReject = async () => {
    if (!article) return;
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setSaving(true);
    try {
      await updateArticle(article.id, {
        reviewStatus: 'rejected',
        rejectionReason: rejectionReason.trim(),
      });
      toast.success('Article returned to author with feedback');
      setShowRejectModal(false);
      onSaved();
    } catch (error) {
      console.error('Error rejecting article:', error);
      toast.error('Failed to reject article');
    } finally {
      setSaving(false);
    }
  };

  const isPromotionExpired = (): boolean => {
    if (!article?.promoted || !article?.promotedUntil) return false;
    return article.promotedUntil.toDate() < new Date();
  };

  const addLink = () => {
    updateForm({ links: [...form.links, { label: '', url: '' }] });
  };

  const updateLink = (index: number, field: keyof ArticleLink, value: string) => {
    const updated = [...form.links];
    updated[index] = { ...updated[index], [field]: value };
    updateForm({ links: updated });
  };

  const removeLink = (index: number) => {
    updateForm({ links: form.links.filter((_, i) => i !== index) });
  };

  // Contributor can submit for review when status is draft or rejected
  const canSubmitForReview = !isAdmin && (isCreating || currentReviewStatus === 'draft' || currentReviewStatus === 'rejected');
  // Contributor can save as draft when status is draft or rejected
  const canSaveDraft = !isAdmin && (isCreating || currentReviewStatus === 'draft' || currentReviewStatus === 'rejected');
  // Contributor cannot edit when article is pending review or accepted
  const isReadOnlyForContributor = !isAdmin && !isCreating && (currentReviewStatus === 'ready_for_review' || currentReviewStatus === 'accepted');
  // Admin can accept/reject when article is pending review
  const canReview = isAdmin && !isCreating && currentReviewStatus === 'ready_for_review';
  // Admin can only enable accepted articles
  const canEnable = isAdmin && (isCreating || currentReviewStatus === 'accepted');

  return (
    <div className="space-y-6">
      {/* Header with back button and actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            title="Back to articles"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </button>
          <h2 className="text-lg font-semibold text-gray-900">
            {isCreating ? 'New Article' : `Edit: ${article.title}`}
          </h2>
          <CopyLinkButton
            url={article ? buildShareUrl('article', article.id) : null}
            noun="article"
          />
          {!isCreating && (
            <Badge variant={reviewStatusBadgeVariant(currentReviewStatus)}>
              {reviewStatusLabel(currentReviewStatus)}
            </Badge>
          )}
          {/*
            Announcing is separate from enabling, so it needs somewhere to live
            that is not the save bar. Once it has happened the control gives way
            to the record of it — there is nothing left to press.
          */}
          {!isCreating && isAdmin && (
            notified ? (
              <span className="text-xs text-gray-500">
                Notification sent
                {notified.at ? ` ${notified.at.toLocaleString()}` : ''}
              </span>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowSendConfirm(true)}
                loading={sending}
                disabled={sending || !article.enabled || article.testMode === true}
                title={
                  !article.enabled
                    ? 'Enable the article first — subscribers would follow a link they cannot open'
                    : article.testMode === true
                      ? 'This article is in test mode, so only admins can see it'
                      : 'Tell every subscriber about this article'
                }
              >
                Send notification
              </Button>
            )
          )}
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onBack}>
            Cancel
          </Button>

          {/* Admin buttons */}
          {isAdmin && (
            <>
              {canReview && (
                <>
                  <Button
                    variant="danger"
                    onClick={() => setShowRejectModal(true)}
                    loading={saving}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleAccept}
                    loading={saving}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Accept
                  </Button>
                </>
              )}
              <Button variant="primary" onClick={() => handleSave()} loading={saving}>
                {isCreating ? 'Create Article' : 'Save Changes'}
              </Button>
            </>
          )}

          {/* Contributor buttons */}
          {!isAdmin && !isReadOnlyForContributor && (
            <>
              {canSaveDraft && (
                <Button variant="secondary" onClick={() => handleSave('draft')} loading={saving}>
                  Save Draft
                </Button>
              )}
              {canSubmitForReview && (
                <Button variant="primary" onClick={() => handleSave('ready_for_review')} loading={saving}>
                  Submit for Review
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Review status banners */}
      {!isCreating && !isAdmin && (
        <>
          {currentReviewStatus === 'draft' && (
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-4">
              <p className="text-sm text-gray-700">
                This article is a <strong>draft</strong>. When you&apos;re ready, click &ldquo;Submit for Review&rdquo; to send it to an admin for approval.
              </p>
            </div>
          )}
          {currentReviewStatus === 'ready_for_review' && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
              <p className="text-sm text-blue-800">
                This article has been <strong>submitted for review</strong> and is awaiting admin approval. You cannot edit it while it&apos;s under review.
              </p>
            </div>
          )}
          {currentReviewStatus === 'rejected' && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4">
              <p className="text-sm text-red-800 font-medium">Changes requested by admin:</p>
              <p className="text-sm text-red-700 mt-1">
                {article.rejectionReason || 'No reason provided.'}
              </p>
              <p className="text-sm text-red-600 mt-2">
                Please address the feedback and resubmit for review.
              </p>
            </div>
          )}
          {currentReviewStatus === 'accepted' && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-4">
              <p className="text-sm text-green-800">
                This article has been <strong>approved</strong> by an admin. An admin will publish it when ready.
              </p>
            </div>
          )}
        </>
      )}

      {/* Admin review banner */}
      {isAdmin && !isCreating && currentReviewStatus === 'ready_for_review' && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
          <p className="text-sm text-blue-800">
            This article is <strong>pending your review</strong>. Review the content below and accept or reject it.
          </p>
        </div>
      )}

      {/* Read-only overlay message for contributors */}
      {isReadOnlyForContributor && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
          <p className="text-sm text-amber-800">
            This article is read-only. {currentReviewStatus === 'ready_for_review' ? 'It is currently under review.' : 'It has been accepted by an admin.'}
          </p>
        </div>
      )}

      {/* Form content */}
      <fieldset disabled={isReadOnlyForContributor}>
        <div className="space-y-6">
          {/* Article Type */}
          <div className="bg-white shadow rounded-lg p-6">
            <label className="block text-sm font-medium text-gray-900">Article Type</label>
            <div className="mt-2 flex gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="article-type"
                  value="rich"
                  checked={form.type === 'rich'}
                  onChange={() => updateForm({ type: 'rich' })}
                  className="text-brand-600 focus:ring-brand-500"
                />
                Rich (inline content)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="article-type"
                  value="external"
                  checked={form.type === 'external'}
                  onChange={() => updateForm({ type: 'external' })}
                  className="text-brand-600 focus:ring-brand-500"
                />
                External (links to URL)
              </label>
            </div>
          </div>

          {/* Core Fields */}
          <div className="bg-white shadow rounded-lg p-6 space-y-6">
            {/* Title */}
            <div>
              <label htmlFor="article-title" className="block text-sm font-medium text-gray-900">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                id="article-title"
                type="text"
                value={form.title}
                onChange={(e) => updateForm({ title: e.target.value })}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            {/* Subtitle */}
            <div>
              <label htmlFor="article-subtitle" className="block text-sm font-medium text-gray-900">
                Subtitle
              </label>
              <input
                id="article-subtitle"
                type="text"
                value={form.subtitle}
                onChange={(e) => updateForm({ subtitle: e.target.value })}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            {/* Excerpt */}
            <div>
              <label htmlFor="article-excerpt" className="block text-sm font-medium text-gray-900">
                Excerpt <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500">Short summary shown on the article card.</p>
              <textarea
                id="article-excerpt"
                value={form.excerpt}
                onChange={(e) => updateForm({ excerpt: e.target.value })}
                rows={2}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            {/* External URL (only for external type) */}
            {form.type === 'external' && (
              <div>
                <label htmlFor="article-external-url" className="block text-sm font-medium text-gray-900">
                  External URL <span className="text-red-500">*</span>
                </label>
                <input
                  id="article-external-url"
                  type="url"
                  value={form.externalUrl}
                  onChange={(e) => updateForm({ externalUrl: e.target.value })}
                  placeholder="https://..."
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            )}
          </div>

          {/* Body (only for rich type) */}
          {form.type === 'rich' && (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-900">
                  Body <span className="text-red-500">*</span>
                  {bodyEditorMode === 'html' && (
                    <span className="text-gray-400 font-normal ml-1">(HTML Source)</span>
                  )}
                </label>
                <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setBodyEditorMode('visual')}
                    className={`px-3 py-1 text-xs font-medium transition-colors ${
                      bodyEditorMode === 'visual'
                        ? 'bg-brand-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Visual
                  </button>
                  <button
                    type="button"
                    onClick={() => setBodyEditorMode('html')}
                    className={`px-3 py-1 text-xs font-medium transition-colors ${
                      bodyEditorMode === 'html'
                        ? 'bg-brand-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    HTML
                  </button>
                </div>
              </div>
              {bodyExpanded ? (
                <div className="flex items-center justify-between rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6">
                  <span className="text-sm text-gray-500">
                    Editing the body in full screen.
                  </span>
                  <Button variant="secondary" size="sm" onClick={() => setBodyExpanded(false)}>
                    Exit full screen
                  </Button>
                </div>
              ) : bodyEditorMode === 'visual' ? (
                <RichTextEditor
                  value={form.body}
                  onChange={(value) => updateForm({ body: value })}
                  minHeight={400}
                  maxHeight={BODY_MAX_HEIGHT}
                  onToggleExpand={() => setBodyExpanded(true)}
                  readOnly={isReadOnlyForContributor}
                  entityLinks
                />
              ) : (
                <textarea
                  value={form.body}
                  onChange={(e) => updateForm({ body: e.target.value })}
                  className="w-full h-[600px] font-mono text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="<h1>Article Title</h1>\n<p>Your content here...</p>"
                  spellCheck={false}
                />
              )}
            </div>
          )}

          {/* Media & Thumbnail */}
          <div className="bg-white shadow rounded-lg p-6 space-y-6">
            {/* Thumbnail — upload to our Storage, or an existing URL */}
            <ImageUrlField
              id="article-thumbnail"
              label={
                <>
                  Thumbnail <span className="text-red-500">*</span>
                </>
              }
              value={form.thumbnailUrl}
              onChange={(thumbnailUrl) => updateForm({ thumbnailUrl })}
            />

            {/* Header Media */}
            <fieldset className="border border-gray-200 rounded-lg p-4">
              <legend className="text-sm font-medium text-gray-900 px-1">Header Media (optional)</legend>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700">Type</label>
                  <select
                    value={form.headerMediaType}
                    onChange={(e) => updateForm({ headerMediaType: e.target.value as 'image' | 'video' | '' })}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="">None</option>
                    <option value="image">Image</option>
                    <option value="video">Video (YouTube)</option>
                  </select>
                </div>
                {form.headerMediaType && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700">
                        {form.headerMediaType === 'video' ? 'YouTube URL' : 'Image'}
                      </label>
                      {form.headerMediaType === 'video' && (
                        <p className="text-xs text-gray-500">
                          Only YouTube URLs are supported (e.g. youtube.com/watch?v=... or youtu.be/...).
                        </p>
                      )}
                      {form.headerMediaType === 'image' ? (
                        // A header image can be uploaded to our Storage too;
                        // a video stays a YouTube URL.
                        <ImageUrlField
                          label={<span className="sr-only">Header image</span>}
                          labelClassName=""
                          value={form.headerMediaUrl}
                          onChange={(headerMediaUrl) => updateForm({ headerMediaUrl })}
                          onAltSuggested={(alt) => {
                            if (!form.headerMediaAltText.trim()) updateForm({ headerMediaAltText: alt });
                          }}
                        />
                      ) : (
                        <input
                          type="url"
                          value={form.headerMediaUrl}
                          onChange={(e) => updateForm({ headerMediaUrl: e.target.value })}
                          placeholder="https://www.youtube.com/watch?v=..."
                          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                      )}
                      {form.headerMediaType === 'video' && form.headerMediaUrl.trim() && (
                        (() => {
                          const videoId = extractYouTubeId(form.headerMediaUrl.trim());
                          if (!videoId) {
                            return (
                              <p className="mt-1 text-xs text-red-600">
                                Not a recognised YouTube URL. Supported: youtube.com/watch, youtu.be, youtube.com/embed, youtube.com/shorts.
                              </p>
                            );
                          }
                          return (
                            <div className="mt-2">
                              <p className="text-xs text-green-600 mb-1">Valid YouTube video (ID: {videoId})</p>
                              <div className="aspect-video w-full max-w-sm rounded overflow-hidden bg-black">
                                <iframe
                                  src={youTubeEmbedUrl(videoId)}
                                  title="YouTube preview"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  className="w-full h-full"
                                />
                              </div>
                            </div>
                          );
                        })()
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700">Alt Text</label>
                      <input
                        type="text"
                        value={form.headerMediaAltText}
                        onChange={(e) => updateForm({ headerMediaAltText: e.target.value })}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                    </div>
                  </>
                )}
              </div>
            </fieldset>
          </div>

          {/* Author */}
          <div className="bg-white shadow rounded-lg p-6">
            <fieldset className="border border-gray-200 rounded-lg p-4">
              <legend className="text-sm font-medium text-gray-900 px-1">Author</legend>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.authorName}
                    onChange={(e) => updateForm({ authorName: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Avatar URL</label>
                  <input
                    type="url"
                    value={form.authorAvatarUrl}
                    onChange={(e) => updateForm({ authorAvatarUrl: e.target.value })}
                    placeholder="https://..."
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Bio</label>
                  <input
                    type="text"
                    value={form.authorBio}
                    onChange={(e) => updateForm({ authorBio: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </div>
            </fieldset>
          </div>

          {/* Tags, Category, Read Time */}
          <div className="bg-white shadow rounded-lg p-6 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="article-tags" className="block text-sm font-medium text-gray-900">Tags</label>
                <p className="text-xs text-gray-500">Comma-separated</p>
                <input
                  id="article-tags"
                  type="text"
                  value={form.tags}
                  onChange={(e) => updateForm({ tags: e.target.value })}
                  placeholder="news, reviews, guides"
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label htmlFor="article-category" className="block text-sm font-medium text-gray-900">Category</label>
                <input
                  id="article-category"
                  type="text"
                  value={form.category}
                  onChange={(e) => updateForm({ category: e.target.value })}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>

            <div>
              <label htmlFor="article-readtime" className="block text-sm font-medium text-gray-900">
                Read Time (minutes)
              </label>
              <input
                id="article-readtime"
                type="number"
                min={1}
                step={1}
                value={form.readTimeMinutes}
                onChange={(e) => updateForm({ readTimeMinutes: e.target.value })}
                className="mt-1 w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          </div>

          {/* Links */}
          <div className="bg-white shadow rounded-lg p-6">
            <fieldset className="border border-gray-200 rounded-lg p-4">
              <legend className="text-sm font-medium text-gray-900 px-1">Links (optional)</legend>
              <div className="space-y-3">
                {form.links.map((link, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <input
                      type="text"
                      value={link.label}
                      onChange={(e) => updateLink(i, 'label', e.target.value)}
                      placeholder="Label"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <input
                      type="url"
                      value={link.url}
                      onChange={(e) => updateLink(i, 'url', e.target.value)}
                      placeholder="https://..."
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <Button variant="danger" size="sm" onClick={() => removeLink(i)}>
                      Remove
                    </Button>
                  </div>
                ))}
                <Button variant="secondary" size="sm" onClick={addLink}>
                  Add Link
                </Button>
              </div>
            </fieldset>
          </div>

          {/* Source & Attribution */}
          <div className="bg-white shadow rounded-lg p-6">
            <fieldset className="border border-gray-200 rounded-lg p-4">
              <legend className="text-sm font-medium text-gray-900 px-1">Source &amp; Attribution (optional)</legend>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">Source Name</label>
                    <p className="text-xs text-gray-500">e.g. &ldquo;Hodinkee&rdquo;, &ldquo;WatchTime&rdquo;</p>
                    <input
                      type="text"
                      value={form.source}
                      onChange={(e) => updateForm({ source: e.target.value })}
                      placeholder="Hodinkee"
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">Source URL</label>
                    <p className="text-xs text-gray-500">Link to original article</p>
                    <input
                      type="url"
                      value={form.sourceUrl}
                      onChange={(e) => updateForm({ sourceUrl: e.target.value })}
                      placeholder="https://..."
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Disclaimer</label>
                  <p className="text-xs text-gray-500">
                    Per-article disclaimer override. Falls back to global disclaimer if not set.
                  </p>
                  <textarea
                    value={form.disclaimer}
                    onChange={(e) => updateForm({ disclaimer: e.target.value })}
                    rows={2}
                    placeholder="Content reproduced with permission from..."
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </div>
            </fieldset>
          </div>

          {/* Toggles: Enabled (admin + accepted only), Featured, signed-out visibility */}
          <div className="bg-white shadow rounded-lg p-6 space-y-4">
            {canEnable && (
              <ToggleField
                id="article-enabled"
                label="Enabled"
                description={
                  form.testMode
                    ? 'Turned off while the article is in test mode.'
                    : 'When disabled, the article is hidden from all users.'
                }
                checked={form.enabled && !form.testMode}
                disabled={form.testMode}
                onChange={(v) => updateForm({ enabled: v })}
              />
            )}
            {isAdmin && (
              <ToggleField
                id="article-test-mode"
                label="Test mode (visible only to admin users)"
                description="Preview on your device without publishing to end users. Uncheck to make live."
                checked={form.testMode}
                onChange={(v) => updateForm({ testMode: v })}
              />
            )}
            <ToggleField
              id="article-featured"
              label="Featured"
              description="Featured articles are highlighted in the mobile app."
              checked={form.featured}
              onChange={(v) => updateForm({ featured: v })}
            />
            {isAdmin && (
              <ToggleField
                id="article-visible-when-anonymous"
                label="Visible to signed-out users"
                description="Shows on the app's landing / sign-in screen and can be read without an account."
                checked={form.visibleWhenAnonymous}
                onChange={(v) => updateForm({ visibleWhenAnonymous: v })}
              />
            )}
          </div>

          {/* Positioning: Pin Order & Promotion (admin only) */}
          {isAdmin && (
            <div className="bg-white shadow rounded-lg p-6">
              <fieldset className="border border-gray-200 rounded-lg p-4">
                <legend className="text-sm font-medium text-gray-900 px-1">Positioning</legend>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="pin-order" className="block text-sm font-medium text-gray-900">
                      Pin Order
                    </label>
                    <p className="text-xs text-gray-500">
                      Lower numbers appear higher in the feed. Leave empty to unpin.
                    </p>
                    <input
                      id="pin-order"
                      type="number"
                      min={1}
                      step={1}
                      value={form.pinOrder}
                      onChange={(e) => updateForm({ pinOrder: e.target.value })}
                      placeholder="Unpinned"
                      className="mt-1 w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>

                  <ToggleField
                    id="article-promoted"
                    label="Promote Article"
                    description="Temporarily boosts the article above all others, including pinned articles."
                    checked={form.promoted}
                    onChange={(v) => {
                      updateForm({ promoted: v, ...(!v && { promotedUntil: '' }) });
                    }}
                  />

                  {form.promoted && (
                    <div>
                      <label htmlFor="promoted-until" className="block text-sm font-medium text-gray-900">
                        Promotion Expires
                      </label>
                      <p className="text-xs text-gray-500">Must be a future date.</p>
                      <input
                        id="promoted-until"
                        type="datetime-local"
                        value={form.promotedUntil}
                        onChange={(e) => updateForm({ promotedUntil: e.target.value })}
                        min={toDatetimeLocalString(new Date())}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                    </div>
                  )}

                  {!isCreating && isPromotionExpired() && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                      <p className="text-sm text-amber-800">
                        This article&apos;s promotion has expired. Toggle promotion off to clear, or set
                        a new future expiry date.
                      </p>
                    </div>
                  )}
                </div>
              </fieldset>
            </div>
          )}

          {/* Bottom save bar */}
          <div className="flex justify-end gap-3 pb-4">
            <Button variant="secondary" onClick={onBack}>
              Cancel
            </Button>
            {isAdmin && (
              <Button variant="primary" onClick={() => handleSave()} loading={saving}>
                {isCreating ? 'Create Article' : 'Save Changes'}
              </Button>
            )}
            {!isAdmin && !isReadOnlyForContributor && (
              <>
                {canSaveDraft && (
                  <Button variant="secondary" onClick={() => handleSave('draft')} loading={saving}>
                    Save Draft
                  </Button>
                )}
                {canSubmitForReview && (
                  <Button variant="primary" onClick={() => handleSave('ready_for_review')} loading={saving}>
                    Submit for Review
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </fieldset>

      {/* Announce on publish */}
      <Modal
        isOpen={showPublishConfirm}
        onClose={() => setShowPublishConfirm(false)}
        title="Send notification to subscribers?"
        size="md"
      >
        <div className="p-2 space-y-4">
          <p className="text-sm text-gray-700">
            This article is about to become visible to everyone. Announcing it sends a push
            notification and an email to every user who has article notifications turned on.
          </p>
          <p className="text-sm text-gray-500">
            A notification cannot be recalled once it has gone out. You can leave it for now and
            send it later from this page.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              disabled={saving}
              onClick={() => {
                setShowPublishConfirm(false);
                void persist(pendingReview, false);
              }}
            >
              Not yet
            </Button>
            <Button
              variant="primary"
              loading={saving}
              onClick={() => {
                setShowPublishConfirm(false);
                void persist(pendingReview, true);
              }}
            >
              Send now
            </Button>
          </div>
        </div>
      </Modal>

      {/* Announce from the header */}
      <Modal
        isOpen={showSendConfirm}
        onClose={() => setShowSendConfirm(false)}
        title="Send notification to subscribers?"
        size="md"
      >
        <div className="p-2 space-y-4">
          <p className="text-sm text-gray-700">
            Every user with article notifications turned on will get a push notification and an
            email about &ldquo;{article?.title}&rdquo;.
          </p>
          <p className="text-sm text-gray-500">This cannot be undone once it has gone out.</p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowSendConfirm(false)} disabled={sending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={sending}
              onClick={async () => {
                if (!article) return;
                setSending(true);
                await notifySubscribers(article.id);
                setSending(false);
                setShowSendConfirm(false);
              }}
            >
              Send notification
            </Button>
          </div>
        </div>
      </Modal>

      {/* Full-screen body editor */}
      <Modal
        isOpen={bodyExpanded}
        onClose={() => setBodyExpanded(false)}
        title="Article body"
        size="full"
        // A stray click beside a long body must not throw the curator out of it.
        // The ✕ and Escape still close, both through `onClose`.
        dismissOnBackdrop={false}
      >
        {bodyEditorMode === 'visual' ? (
          <RichTextEditor
            value={form.body}
            onChange={(value) => updateForm({ body: value })}
            minHeight={400}
            // Under the Modal body's own `max-h-[80vh] overflow-y-auto`, so it
            // fits inside rather than making a scroll box within a scroll box.
            maxHeight="65vh"
            onToggleExpand={() => setBodyExpanded(false)}
            expanded
            readOnly={isReadOnlyForContributor}
            entityLinks
          />
        ) : (
          <textarea
            value={form.body}
            onChange={(e) => updateForm({ body: e.target.value })}
            className="w-full h-[65vh] font-mono text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            spellCheck={false}
          />
        )}
        <div className="mt-4 flex justify-end">
          <Button variant="secondary" onClick={() => setBodyExpanded(false)}>
            Done
          </Button>
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject Article"
        size="md"
      >
        <div className="space-y-4 p-2">
          <p className="text-sm text-gray-700">
            Provide feedback for the author explaining what needs to be changed.
          </p>
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={4}
            placeholder="Please explain what changes are needed..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleReject} loading={saving}>
              Reject with Feedback
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function ToggleField({
  id,
  label,
  description,
  checked,
  onChange,
  disabled = false,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <label
          htmlFor={id}
          className={`text-sm font-medium ${disabled ? 'text-gray-400' : 'text-gray-900'}`}
        >
          {label}
        </label>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        } ${checked ? 'bg-brand-600' : 'bg-gray-200'}`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

function toDatetimeLocalString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
